import { useEffect, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { IntentRead } from '../../types/api';
import { fetchIntents } from './generationApi';

/**
 * The intent registry, loaded once per session.
 *
 * Global and unchanging while the app is open — and read by more surfaces at
 * once than the vibes are (a review screen asks for it twice, once per variant
 * card) — so the fetch is cached in a module-level promise like
 * `useFeedCatalog`'s: five consumers are one request. A failed load clears the
 * cache, so the next mount really retries.
 */
let intentsPromise: Promise<IntentRead[]> | null = null;

function loadIntents(): Promise<IntentRead[]> {
  if (intentsPromise === null) {
    intentsPromise = fetchIntents().catch((caught: unknown) => {
      intentsPromise = null;
      throw caught;
    });
  }
  return intentsPromise;
}

export interface IntentsResult {
  /** The registry in the server's own order. Empty while loading or on failure. */
  intents: IntentRead[];
  isLoading: boolean;
  error: string | null;
}

/** Stable empty result, so consumers do not see a new array identity per render. */
const NO_INTENTS: never[] = [];

/**
 * The post-intent vocabulary behind every intent picker and every intent label.
 *
 * Shape follows {@link useVibes}: one effect, no synchronous state write,
 * visible state derived during render. A failure is not fatal anywhere —
 * intent is optional on a brief, so a surface that cannot read the list simply
 * offers nothing and the model decides, exactly as it did before intents
 * existed.
 */
export function useIntents(): IntentsResult {
  const [loaded, setLoaded] = useState<IntentRead[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const intents = await loadIntents();
        if (cancelled) return;
        setLoaded(intents);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        setLoaded(NO_INTENTS);
        setError(getErrorMessage(caught, 'Could not load the list of post purposes.'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    intents: loaded ?? NO_INTENTS,
    isLoading: loaded === null,
    error,
  };
}
